import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Chip,
  ListItemIcon,
  Avatar,
  useTheme,
  Badge,
  Popover,
  Divider,
  Button,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuthenticatedFilePreview } from '../../hooks/useAuthenticatedFilePreview';
import { useThemeMode } from '../../hooks/ThemeContext';
import { useNotifies } from '../../hooks/NotifyContext';
import { DRAWER_WIDTH } from './Sidebar';

interface TopBarProps {
  onOpenSearch?: () => void;
  onDrawerToggle?: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onOpenSearch, onDrawerToggle }) => {
  const { user, logout } = useAuth();
  const avatarFileId = user?.profil?.avatar_file_id ?? null;
  const avatarUrl = useAuthenticatedFilePreview(avatarFileId);
  const { mode, toggleTheme } = useThemeMode();
  const { notifies, unreadCount, hasImportant, markAsRead, markAllRead, refresh } = useNotifies();
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifyAnchor, setNotifyAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (notifyAnchor) {
      void refresh();
    }
  }, [notifyAnchor, refresh]);

  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const borderColor = theme.palette.divider;
  const bgSidebar = isDark ? '#1c203b' : '#ffffff';
  const sidebarText = isDark ? '#b0bec5' : '#3a4a5a';
  const sidebarIcon = isDark ? '#8899aa' : '#6b7c8d';

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
        ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
        bgcolor: bgSidebar,
        borderBottom: `1px solid ${borderColor}`,
      }}
    >
      <Toolbar>
        {/* Hamburger menu - mobile only */}
        {onDrawerToggle && (
          <IconButton
            color="inherit"
            aria-label="Menü öffnen"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ mr: 1, display: { xs: 'flex', md: 'none' }, color: sidebarIcon }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {/* Search bar trigger */}
        {onOpenSearch && (
          <Box
            onClick={onOpenSearch}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              maxWidth: { xs: 360, sm: 720 },
              minWidth: { xs: 140, sm: 420 },
              height: 42,
              px: 2,
              gap: 1.5,
              cursor: 'pointer',
              borderRadius: 2.5,
              border: `1.5px solid ${borderColor}`,
              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: accent,
                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                '& .search-icon': {
                  color: accent,
                },
                '& .search-text': {
                  color: isDark ? '#cfd8dc' : '#455a64',
                },
              },
            }}
          >
            <SearchIcon
              className="search-icon"
              sx={{
                color: sidebarIcon,
                fontSize: '1.3rem',
                transition: 'color 0.2s ease',
              }}
            />
            <Typography
              className="search-text"
              sx={{
                flex: 1,
                color: sidebarIcon,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 400,
                userSelect: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                transition: 'color 0.2s ease',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Suche nach Projekten, Kunden, Diskussionen, Audits...
            </Typography>
            <Typography
              className="search-text"
              sx={{
                flex: 1,
                color: sidebarIcon,
                fontSize: '0.8rem',
                fontWeight: 400,
                userSelect: 'none',
                display: { xs: 'block', sm: 'none' },
              }}
            >
              Suche...
            </Typography>
            <Chip
              label="Strg+K"
              size="small"
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: isDark ? '#2e3560' : '#e0e4e8',
                color: sidebarIcon,
                borderRadius: 1.5,
                display: { xs: 'none', sm: 'flex' },
              }}
            />
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Notify bell */}
            <IconButton
              onClick={(e) => setNotifyAnchor(e.currentTarget)}
              size="small"
              sx={{
                color: sidebarIcon,
                '&:hover': { color: hasImportant ? '#f57c00' : accent },
              }}
              title="Benachrichtigungen"
            >
              <Badge
                badgeContent={unreadCount}
                max={99}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: hasImportant ? '#f57c00' : accent,
                    color: '#fff',
                    fontSize: '0.65rem',
                    minWidth: 18,
                    height: 18,
                  },
                }}
              >
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>

            <Popover
              open={Boolean(notifyAnchor)}
              anchorEl={notifyAnchor}
              onClose={() => setNotifyAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    width: 380,
                    maxHeight: 480,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Benachrichtigungen{' '}
                  {unreadCount > 0 &&
                    (unreadCount > notifies.length
                      ? `(${notifies.length} von ${unreadCount})`
                      : `(${unreadCount})`)}
                </Typography>
                {unreadCount > 0 && (
                  <Button
                    size="small"
                    startIcon={<DoneAllIcon />}
                    onClick={async () => { await markAllRead(); }}
                    sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                  >
                    Alle gelesen
                  </Button>
                )}
              </Box>
              <Divider />
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {notifies.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Keine ungelesenen Benachrichtigungen
                    </Typography>
                  </Box>
                ) : (
                  notifies.map((n) => (
                    <Box
                      key={n.id}
                      sx={{
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: n.typ === 'important'
                          ? (isDark ? 'rgba(245,124,0,0.08)' : 'rgba(245,124,0,0.05)')
                          : 'transparent',
                        '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' },
                      }}
                    >
                      {n.typ === 'important' ? (
                        <WarningAmberIcon sx={{ color: '#f57c00', fontSize: 20, mt: 0.3, flexShrink: 0 }} />
                      ) : (
                        <NotificationsIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.3, flexShrink: 0 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {n.title}
                        </Typography>
                        {n.message && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.3 }}>
                            {n.message.length > 120 ? n.message.slice(0, 120) + '...' : n.message}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
                          {new Date(n.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => markAsRead(n.id)}
                          title="Als gelesen markieren"
                          sx={{ color: 'text.secondary', '&:hover': { color: accent } }}
                        >
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        {n.link && (
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                await markAsRead(n.id);
                              } catch {
                                // ignore
                              }
                              setNotifyAnchor(null);
                              navigate(n.link!);
                            }}
                            title="Öffnen"
                            sx={{ color: 'text.secondary', '&:hover': { color: accent } }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Popover>

            {/* Theme toggle */}
            <IconButton
              onClick={toggleTheme}
              size="small"
              sx={{
                color: sidebarIcon,
                '&:hover': { color: accent },
              }}
              title={isDark ? 'Light Mode' : 'Dark Mode'}
            >
              {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>

            {user.rollen && user.rollen.length > 0 ? (
              user.rollen.map((rolle) => (
                <Chip
                  key={rolle.id}
                  label={rolle.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor: isDark ? '#2c3358' : '#eef1f5',
                    color: accent,
                    border: `1px solid ${borderColor}`,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    ml: 0.5,
                    display: { xs: 'none', sm: 'flex' },
                  }}
                />
              ))
            ) : (
              <Chip
                label="Mitarbeiter"
                size="small"
                variant="outlined"
                sx={{
                  bgcolor: isDark ? '#2c3358' : '#eef1f5',
                  color: accent,
                  border: `1px solid ${borderColor}`,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
            )}
            <Typography variant="body2" sx={{ color: sidebarText, fontWeight: 500, display: { xs: 'none', sm: 'block' } }}>
              {user.vorname} {user.nachname}
            </Typography>
            <IconButton
              onClick={handleMenu}
              aria-label="Benutzerkonto"
              sx={{ color: sidebarIcon, '&:hover': { color: accent }, p: 0.25 }}
            >
              {avatarFileId ? (
                <Avatar
                  src={avatarUrl}
                  alt={`${user.vorname} ${user.nachname}`}
                  sx={{ width: 32, height: 32, bgcolor: isDark ? '#2c3358' : '#eef1f5', color: accent }}
                >
                  <AccountCircleIcon fontSize="small" />
                </Avatar>
              ) : (
                <AccountCircleIcon />
              )}
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => { handleClose(); navigate('/profil'); }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                Profil
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon sx={{ color: '#ef5350' }} fontSize="small" />
                </ListItemIcon>
                Abmelden
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
